import { Provider } from 'react-redux';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import { useMemo } from 'react';
import { store } from './store';
import AppRouter from './components/navigation/AppRouter';
import { AuthProvider } from './features/auth/context/auth-context';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './components/ThemeProvider';
import { BrandingProvider } from './components/BrandingProvider';
import { BrandingInitializer } from './components/BrandingInitializer';
import { Toaster } from './components/ui/sonner';
import { brandColors, typography } from './theme';
import { useTheme, useAppSelector } from '@/hooks';
import { generateColorVariations, addAlpha } from './utils/branding';

// Create Material-UI theme using centralized brand colors, reactive to Redux theme and branding
function useMuiTheme() {
  const { theme: mode } = useTheme();
  const branding = useAppSelector((state) => state.branding);
  
  const muiTheme = useMemo(() => {
    const isDark = mode === 'dark';
    const textPrimary = isDark ? '#FAFAFA' : brandColors.text.primary;
    const textSecondary = isDark ? '#A1A1AA' : brandColors.text.secondary;

    // Use branding colors if available, otherwise fallback to default brandColors
    const primaryVariations = branding.isLoaded
      ? generateColorVariations(branding.primaryColor, isDark)
      : brandColors.primary;
    
    const secondaryVariations = branding.isLoaded
      ? generateColorVariations(branding.secondaryColor, isDark)
      : brandColors.secondary;

    /* Match Tailwind `index.css` shells — faint primary wash + softer borders */
    const primaryForShell = isDark ? secondaryVariations.main : primaryVariations.main;
    const backgroundDefault = isDark
      ? `color-mix(in srgb, ${primaryForShell} 14%, #09090b 86%)`
      : `color-mix(in srgb, ${primaryForShell} 4.5%, white 95.5%)`;
    const backgroundPaper = isDark
      ? `color-mix(in srgb, #18181b 88%, ${primaryForShell} 12%)`
      : `color-mix(in srgb, white 97%, ${primaryForShell} 3%)`;
    const borderColor = isDark
      ? `color-mix(in srgb, ${primaryForShell} 16%, #3f3f46 84%)`
      : `color-mix(in srgb, ${primaryForShell} 7%, #e4e4e7 93%)`;

    return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryVariations.main,
        light: primaryVariations.light,
        dark: primaryVariations.dark,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: secondaryVariations.main,
        light: secondaryVariations.light,
        dark: secondaryVariations.dark,
        contrastText: '#FFFFFF',
      },
        background: { default: backgroundDefault, paper: backgroundPaper },
        text: { primary: textPrimary, secondary: textSecondary },
      grey: brandColors.grey,
    },
    typography: {
      fontFamily: typography.fontFamily,
      h1: {
        fontWeight: typography.fontWeights.bold,
        color: textPrimary,
      },
      h2: {
        fontWeight: typography.heading.fontWeight,
        color: textPrimary,
      },
      h3: {
        fontWeight: typography.heading.fontWeight,
        color: textPrimary,
      },
      h4: {
        fontWeight: typography.heading.fontWeight,
        color: textPrimary,
      },
      h5: {
        fontWeight: typography.heading.fontWeight,
        color: textPrimary,
      },
      h6: {
        fontWeight: typography.heading.fontWeight,
        color: textPrimary,
      },
      body1: {
        color: textPrimary,
      },
      body2: {
        color: textSecondary,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 12,
            padding: '12px 24px',
          },
          contained: {
            backgroundColor: primaryVariations.dark,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: primaryVariations.main,
            },
          },
          outlined: {
            borderColor: primaryVariations.main,
            color: primaryVariations.main,
            '&:hover': {
              backgroundColor: isDark ? '#27272A' : secondaryVariations.light,
              borderColor: secondaryVariations.main,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: backgroundPaper,
            borderRadius: 16,
            boxShadow: `0 4px 20px ${addAlpha(primaryVariations.main, 0.08)}`,
            '&:hover': {
              boxShadow: `0 8px 30px ${addAlpha(primaryVariations.main, 0.15)}`,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: backgroundDefault,
            color: textPrimary,
            boxShadow: `0 2px 8px ${addAlpha(primaryVariations.main, 0.08)}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: backgroundDefault,
            borderRight: `1px solid ${borderColor}`,
          },
        },
      },
    },
    });
  }, [mode, branding.primaryColor, branding.secondaryColor, branding.isLoaded]);
  return muiTheme;
}

function ThemedApp() {
  const theme = useMuiTheme();
  return (
    <ThemeProvider>
      {/* Material-UI ThemeProvider is kept for dashboard pages that use MUI components */}
      {/* CssBaseline removed to prevent conflicts with Tailwind/Radix UI styling */}
      <MuiThemeProvider theme={theme}>
        <BrandingProvider>
          <AuthProvider>
            <BrandingInitializer />
            <AppRouter />
            <Toaster />
          </AuthProvider>
        </BrandingProvider>
      </MuiThemeProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ThemedApp />
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
